import React, { useState } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { Button } from 'src/components/ui/button';
import DataTable from 'src/components/ui/data-table';
import { Plus, Pencil, Trash2, ClipboardList } from 'lucide-react';
import { StatusBadge } from 'src/components/common/StatusBadge';
import { Alert } from 'src/components/common/Alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { useAssessmentTypes } from 'src/services/api/queries/assessmentTypeQueries';
import {
  useCreateAssessmentType,
  useUpdateAssessmentType,
  useDeleteAssessmentType,
} from 'src/services/api/mutations/assessmentTypeMutations';
import { useToast } from 'src/hooks/use-toast';
import { useApiError } from 'src/hooks/useApiError';
import { AssessmentTypeModal } from 'src/components/Modals/AssessmentTypeModal';

const StatusCell = ({ enabled }) => (
  <StatusBadge
    value={enabled ? 'ACTIVE' : 'INACTIVE'}
    label={enabled ? 'Enabled' : 'Disabled'}
  />
);

const ActionsCell = ({ row, onEdit, onDeleteClick }) => (
  <div className="flex items-center gap-1">
    <Button
      variant="ghost"
      size="icon"
      className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity w-7 h-7 rounded-md hover:bg-muted"
      title="Edit assessment type"
      onClick={(e) => {
        e.stopPropagation();
        onEdit(row);
      }}
    >
      <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
    </Button>
    <Button
      variant="ghost"
      size="icon"
      className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity w-7 h-7 rounded-md hover:bg-destructive/10"
      title="Delete assessment type"
      onClick={(e) => {
        e.stopPropagation();
        onDeleteClick(row);
      }}
    >
      <Trash2 className="h-3.5 w-3.5 text-destructive" />
    </Button>
  </div>
);

const createColumns = (handleEditClick, handleDeleteClick) => [
  {
    key: 'code',
    header: 'Code',
    minWidth: '180px',
    render: (v) => (
      <span className="font-mono text-[12px] font-semibold text-foreground-strong">
        {v}
      </span>
    ),
  },
  {
    key: 'display_name',
    header: 'Display Name',
    minWidth: '200px',
    render: (v) => (
      <span className="text-[13px] font-[550] text-foreground-strong">{v}</span>
    ),
  },
  {
    key: 'description',
    header: 'Description',
    minWidth: '220px',
    render: (v) => (
      <div className="min-w-0 w-full">
        <span
          className="block text-[12.5px] text-muted-foreground truncate"
          title={v}
        >
          {v || <span className="italic text-muted-foreground">—</span>}
        </span>
      </div>
    ),
  },
  {
    key: 'enabled',
    header: 'Status',
    width: '120px',
    minWidth: '110px',
    render: (v) => <StatusCell enabled={v} />,
  },
  {
    key: 'actions',
    header: '',
    width: '80px',
    minWidth: '80px',
    render: (_v, row) => (
      <ActionsCell
        row={row}
        onEdit={handleEditClick}
        onDeleteClick={handleDeleteClick}
      />
    ),
  },
];

function AssessmentTypes() {
  const { toast } = useToast();
  const { handleError } = useApiError();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);

  const { data, isLoading, refetch } = useAssessmentTypes();
  const assessmentTypes = data?.data ?? data ?? [];

  const createMutation = useCreateAssessmentType();
  const updateMutation = useUpdateAssessmentType();
  const deleteMutation = useDeleteAssessmentType();

  const handleCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (row) => {
    setEditingItem(row);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingItem) {
        await updateMutation.mutateAsync({
          code: editingItem.code,
          data: formData,
        });
        toast({
          title: 'Success',
          description: 'Assessment type updated successfully.',
          variant: 'success',
        });
      } else {
        await createMutation.mutateAsync(formData);
        toast({
          title: 'Success',
          description: 'Assessment type created successfully.',
          variant: 'success',
        });
      }
      handleModalClose();
      refetch();
    } catch (error) {
      handleError(error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingItem) return;
    try {
      await deleteMutation.mutateAsync(deletingItem.code);
      toast({
        title: 'Success',
        description: 'Assessment type deleted successfully.',
        variant: 'success',
      });
      refetch();
    } catch (error) {
      // Backend refuses with 400 if a test collection group still references
      // this type — handleError surfaces that real message rather than a
      // generic "failed to delete" toast.
      handleError(error);
    } finally {
      setDeletingItem(null);
    }
  };

  const columns = createColumns(handleEditClick, setDeletingItem);

  const emptyState = (
    <div className="bg-card rounded-xl border border-border flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-5">
        <ClipboardList
          className="w-7 h-7 text-muted-foreground"
          strokeWidth={1.5}
        />
      </div>
      <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-tight">
        No assessment types found
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs mb-5 leading-relaxed">
        Get started by adding your first assessment type.
      </p>
      <button
        type="button"
        onClick={handleCreate}
        className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-background text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-all cursor-pointer"
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={2} />
        Add Assessment Type
      </button>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Assessment Types"
        actions={
          <button
            type="button"
            onClick={handleCreate}
            className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-card text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-[border-color,color,background-color] cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2} />
            Add Assessment Type
          </button>
        }
      />

      <PageContainer className="space-y-5">
        <DataTable
          data={assessmentTypes}
          columns={columns}
          loading={isLoading}
          tableLayout="auto"
          className="rounded-xl overflow-hidden"
          emptyState={emptyState}
          rowClassName={() =>
            'hover:bg-brand-bg-subtle transition-colors group'
          }
        />
      </PageContainer>

      <Dialog open={isModalOpen} onOpenChange={handleModalClose}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit Assessment Type' : 'Add Assessment Type'}
            </DialogTitle>
            <DialogDescription>
              {editingItem
                ? `Editing: ${editingItem.display_name}`
                : 'Add a new assessment type to the catalog.'}
            </DialogDescription>
          </DialogHeader>
          <AssessmentTypeModal
            initialData={editingItem}
            onSubmit={handleSubmit}
            onCancel={handleModalClose}
            loading={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      <Alert
        isOpen={!!deletingItem}
        onClose={() => setDeletingItem(null)}
        title="Delete assessment type?"
        description={
          deletingItem
            ? `"${deletingItem.display_name}" (${deletingItem.code}) will be removed from the catalog. This can't be undone, and fails if any test collection group still uses it.`
            : ''
        }
        cancelText="Cancel"
        continueText="Delete"
        onCancel={() => setDeletingItem(null)}
        onContinue={handleDeleteConfirm}
      />
    </div>
  );
}

export default AssessmentTypes;
